import { useState } from 'react';
import { Alert, Button, Card, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { changePassword } from '../api/pa';
import { errorText } from '../session/errorText';
import { useSession } from '../session/SessionProvider';

/**
 * Change the portal password (`ui-enduser-personal-area` §5; Appendix L §4 **F3**).
 *
 * Three fields, as the spec's table says — and only two of them are sent. The
 * confirmation is compared in the browser and never leaves it: «a server that receives
 * two copies of a new password can only compare them, which the browser already did»
 * (handlers.go). The CURRENT password is required even though the caller holds a
 * session, because a session proves who he is and not that he knows the password — it is
 * what stops a session left behind in a shared browser from locking the owner out.
 *
 * ⚠ C18 IS NOT RE-IMPLEMENTED HERE, AND THAT IS THE DESIGN. The character set (printable
 * ASCII without a space and without `" ' \ | / % $`) is enforced by `internal/users` and
 * reported as `400 pa.password_charset` with a Russian message this screen displays
 * verbatim. Two reasons it is not also a client-side rule:
 *
 *   - the server is the one that MUST hold, so a second copy can only drift from it —
 *     and the copy that drifts is the one the subscriber believes;
 *   - the refusal is honest here (unlike on the login route) precisely because the caller
 *     is already authenticated, so there is nothing to hide and no reason to pre-empt it.
 *
 * There is no charset HINT either, for the same reason: a hint is a copy of the rule.
 * `PasswordScreen.test.tsx` scans this file for a charset regex and fails if one appears.
 *
 * After a successful change the server REVOKES every other session of this subscriber
 * and clears `password_temporary`, so the screen re-reads /me: without that, the
 * «смените пароль» prompt on the home page would still be showing after the subscriber
 * had done exactly what it asked.
 */
export function PasswordScreen() {
  const { t } = useTranslation();
  const { refresh } = useSession();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  const submit = async (values: { current: string; next: string }) => {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await changePassword(values.current, values.next);
      setDone(true);
      form.resetFields();
      // The identity carries `must_change_password`; the change cleared it server-side.
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1 className="pa-page-title">{t('password.title')}</h1>
      <p className="pa-page-lead">{t('password.lead')}</p>

      <Card>
        {done ? (
          <Alert
            type="success"
            showIcon
            message={t('password.success')}
            style={{ marginBottom: 20 }}
          />
        ) : null}
        {error ? (
          <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 20 }} />
        ) : null}

        <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={busy}>
          <Form.Item
            name="current"
            label={t('password.currentLabel')}
            rules={[{ required: true, message: t('common.required') }]}
          >
            <Input.Password autoComplete="current-password" size="large" />
          </Form.Item>

          <Form.Item
            name="next"
            label={t('password.newLabel')}
            rules={[{ required: true, message: t('common.required') }]}
          >
            {/* No `pattern` rule. See the header: C18 lives in the server. */}
            <Input.Password autoComplete="new-password" size="large" />
          </Form.Item>

          <Form.Item
            name="confirm"
            label={t('password.confirmLabel')}
            dependencies={['next']}
            rules={[
              { required: true, message: t('common.required') },
              // The ONE client-side rule, and it is not a copy of a server rule: the
              // server never sees this field, so if the browser does not compare the two
              // nothing does.
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || getFieldValue('next') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('password.mismatch'))),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" size="large" />
          </Form.Item>

          <Button type="primary" htmlType="submit" size="large" block loading={busy}>
            {t('password.submit')}
          </Button>
        </Form>
      </Card>
    </>
  );
}
